import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./header.html";
import { NavLevel, MenuPane } from '@oneappexchange/appx-design-system/modules/ax/marketplaceHeader/marketplaceHeader.constants';
import { WireStore } from '@oneappexchange/lwc-wired';
import equalityFn from 'zustand/shallow';
import { FAVORITES_MENU_IDENTIFIER } from '~/components/ace/favorites/favorites';
import { USER_MENU_IDENTIFIER } from '~/components/ace/userMenu/userMenu';
import { createEvent } from '~/modules/analytics/analyticsHelper';
import { storeAuthenticationContext } from '~/modules/analytics/authenticationContext';
import { ElementType } from '~/modules/analytics/gtmEventTypes/constants';
import { AGENTEXCHANGE_JP_SITE_URL, AGENTEXCHANGE_SITE_URL, APPX_JP_SITE_URL, APPX_SITE_URL, IS_DEVELOPMENT, SLACK_FORCE_MARKETPLACE_SWITCHER_JP, SLACK_MARKETPLACE_URL, TBID_MOCK_USER } from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { isExternalAgentDiscoveryExperienceAvailable } from '~/modules/helpers/externalAgentSearchHelper';
import { getUserLanguage } from '~/modules/helpers/languageHelper';
import { withLocalePrefix } from '~/modules/helpers/localeHelper';
import { getSlackUtmSource } from '~/modules/helpers/slackMarketplaceLinkHelper';
import { areSameOrgIds, getUpdatedSelectedOrg, getValidatedSelectedOrg, isStaleSelectedOrg } from '~/modules/helpers/orgContextHelper';
import { login as tbidLogin, signup as tbidSignup } from '~/modules/helpers/routingHelper';
import { LEARN_BASE_ROUTE, Routes } from '~/modules/routes';
import { WireI18n, i18nStore, selectSharedLabels } from '~/modules/stores/i18nStore';
import { getOrgEditionBadgeTheme, getOrgName, hasOrgDomain, hasOrgEdition } from '~/modules/helpers/orgCardFields';
import { switchToOrgPicker } from '~/modules/helpers/orgSwitchDropOff';
import { orgContextStore, selectSelectedOrg } from '~/modules/stores/orgContextStore';
import { orgPickerStore } from '~/modules/stores/orgPickerStore';
import { navigate } from '~/modules/stores/routerStore';
import { selectIsAgentExchange, siteConfigStore } from '~/modules/stores/siteConfigStore';
import { selectIsAuthenticated, userStore } from '~/modules/stores/userStore';
import { featureFlagsStore } from '~/modules/stores/feature_flags_store';
import { JAPANESE_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
import { isOrgPickerEnabled, ORG_PICKER_PILOT_CHANGE_EVENT, ORG_PICKER_PILOT_KEY } from '~/modules/utilities/orgPickerPilot';
import { sendNotification } from '~/modules/utilities/notificationUtilities';
import { parseJson } from '~/modules/utilities/object_utils';
import { buildUrl } from '~/modules/utilities/route_utils';
export let MarketplaceId = /*#__PURE__*/function (MarketplaceId) {
  MarketplaceId["SalesforceMarketplace"] = "salesforce-marketplace";
  MarketplaceId["SlackMarketplace"] = "slack-marketplace";
  return MarketplaceId;
}({});
const BUCKET_ICON_MAP = {
  'business-needs': 'business-needs',
  products: 'products',
  industries: 'industries',
  impact: 'impact',
  'salesforce-labs': 'labs'
};
const MOBILE_MENU_PANES = {
  HELP_CENTER: 'help-center',
  USER_MENU: 'user-menu'
};
class Header extends LightningElement {
  constructor(...args) {
    super(...args);
    this.isAgentExchange = void 0;
    this.isLoggedIn = false;
    this.orgPickerEnabled = false;
    this.handleOrgPickerPilotChange = () => this.refreshOrgPickerEnabled();
    this.handleOrgPickerPilotStorageChange = event => {
      if (event.storageArea === localStorage && (event.key === ORG_PICKER_PILOT_KEY || event.key === null)) {
        this.refreshOrgPickerEnabled();
      }
    };
    this.staleSelectionClearedOrgId = null;
    this.selectedOrg = null;
    // Must have a runtime initializer so LWC observes the tenant-count update and re-renders
    // the unselected multi-org header state. A TypeScript-only declaration is erased in the ES6 build.
    this.orgCount = 0;
    this.selectedMobileMenuPane = void 0;
  }
  setUserContext({
    isAuthenticated,
    tbidProfile
  }) {
    this.isLoggedIn = isAuthenticated;
    this.userContext = {
      tbidProfile
    };
    this.refreshOrgPickerEnabled();
  }

  // FeatureFlags is an imperative getter, so observe its backing store to re-evaluate the
  // header when the authenticated flag context arrives after the user-store auth event.
  setOrgPickerFeatureFlag() {
    this.refreshOrgPickerEnabled();
  }
  refreshOrgPickerEnabled() {
    this.orgPickerEnabled = isOrgPickerEnabled();
    if (this.isLoggedIn && this.orgPickerEnabled) {
      // The store shares an in-flight request and skips once loaded, so auth, flag, and
      // pilot signals can all safely converge here without duplicate tenant requests.
      this.loadOrgCount();
    }
  }
  async loadOrgCount() {
    await orgPickerStore.loadTenants();
    // The user may have logged out or changed locale while the request was in flight.
    if (!this.isLoggedIn || !this.orgPickerEnabled) {
      return;
    }
    const {
      totalCount,
      orgs
    } = orgPickerStore.getState();
    this.orgCount = Math.max(totalCount, orgs.length);
    this.clearStaleSelectedOrg();
  }
  clearStaleSelectedOrg() {
    const {
      totalCount,
      orgs,
      hasLoaded
    } = orgPickerStore.getState();
    const selectedOrg = this.selectedOrg;
    // orgs.length === totalCount guards against a partial/paginated orgs list being
    // misread as staleness — totalCount is the service-reported count, not derived from
    // orgs.length (userService.ts), so the two are only safe to compare when they agree.
    if (!hasLoaded || !this.orgPickerEnabled || orgs.length !== totalCount || selectedOrg === null) {
      return;
    }
    if (isStaleSelectedOrg(selectedOrg, orgs)) {
      // Guard against re-clearing (and re-notifying) the same selection across the two wires
      // that call this — setSelectedOrg resets the guard when the orgId actually changes.
      if (selectedOrg.orgId === this.staleSelectionClearedOrgId) {
        return;
      }
      this.staleSelectionClearedOrgId = selectedOrg.orgId;
      orgContextStore.clearSelectedOrg();
      sendNotification(this.notificationLabels?.orgPicker?.staleOrgCleared?.successMessage ?? '');
      return;
    }
    // The persisted selection (up to 7-day-old cookie) can still match an orgId while other
    // fields — most visibly the edition badge — changed server-side since it was cached.
    const updatedOrg = getUpdatedSelectedOrg(selectedOrg, orgs);
    if (updatedOrg) {
      orgContextStore.setSelectedOrg(updatedOrg);
    }
    const orgProfile = orgContextStore.getState().orgProfile;
    if (!orgProfile || !areSameOrgIds(orgProfile.orgId, selectedOrg.orgId)) {
      orgContextStore.loadOrgProfile(selectedOrg.orgId);
    }
  }
  setSelectedOrg(selectedOrg) {
    if (this.selectedOrg?.orgId !== selectedOrg?.orgId) {
      this.staleSelectionClearedOrgId = null;
    }
    this.selectedOrg = selectedOrg;
    this.clearStaleSelectedOrg();
  }

  // Keep the menu visibility input on the scalar count. This must remain a scalar wire: the
  // badge only depends on the count, and a richer selector previously prevented the visible
  // header from receiving a completed tenant load in production.
  setOrgCount(orgCount) {
    this.orgCount = orgCount;
  }
  get hasValidatedSelectedOrg() {
    return Boolean(getValidatedSelectedOrg(this.selectedOrg, orgPickerStore.getState()));
  }

  // Multi-org users get the org-context menu (showing "Select Org" until they pick); single-org
  // users keep the user menu. Gated by the EN-only org-picker gate (flag OR pilot enrollment).
  get showOrgContextMenu() {
    return this.isLoggedIn && this.orgPickerEnabled && this.orgCount > 1;
  }

  // Org card shown above the user row in the mobile menu. Gated the same way as the desktop
  // org-context menu (shared org-picker gate: flag or pilot enrollment, English-only) and only
  // once an org is selected.
  get showOrgCard() {
    return this.isLoggedIn && this.orgPickerEnabled && this.orgCount > 1 && this.hasValidatedSelectedOrg;
  }
  connectedCallback() {
    window.addEventListener(ORG_PICKER_PILOT_CHANGE_EVENT, this.handleOrgPickerPilotChange);
    window.addEventListener('storage', this.handleOrgPickerPilotStorageChange);
    this.refreshOrgPickerEnabled();
  }
  disconnectedCallback() {
    window.removeEventListener(ORG_PICKER_PILOT_CHANGE_EVENT, this.handleOrgPickerPilotChange);
    window.removeEventListener('storage', this.handleOrgPickerPilotStorageChange);
  }
  get orgName() {
    return getOrgName(this.selectedOrg);
  }
  get hasEdition() {
    return hasOrgEdition(this.selectedOrg);
  }
  get hasDomain() {
    return hasOrgDomain(this.selectedOrg);
  }
  get editionBadgeTheme() {
    return getOrgEditionBadgeTheme(this.selectedOrg);
  }
  get switchOrgLabel() {
    return this.orgContextMenuLabels?.switchOrg ?? '';
  }
  get orgIconDescription() {
    return this.orgContextMenuLabels?.orgIconDescription ?? '';
  }
  setupLabels({
    header,
    shared,
    orgContextMenu
  }) {
    this.labels = header;
    this.sharedLabels = shared;
    this.orgContextMenuLabels = orgContextMenu;
  }
  setNotificationLabels(notifications) {
    this.notificationLabels = notifications;
  }
  saveAuthenticationContext() {
    storeAuthenticationContext({
      origin: 'header'
    });
  }
  login() {
    this.saveAuthenticationContext();
    if (IS_DEVELOPMENT && TBID_MOCK_USER) {
      userStore.setMockedUser(parseJson(TBID_MOCK_USER));
      return;
    }
    tbidLogin();
  }
  signup() {
    this.saveAuthenticationContext();
    tbidSignup();
  }
  get showMobileHelpCenterMenu() {
    return this.selectedMobileMenuPane === MOBILE_MENU_PANES.HELP_CENTER;
  }
  get showMobileUserMenu() {
    return this.selectedMobileMenuPane === MOBILE_MENU_PANES.USER_MENU;
  }
  get learnUrl() {
    if (FeatureFlags.FF_JAPANESE_SITE_LEARN_TAB) {
      return `/${LEARN_BASE_ROUTE}`;
    }
    return this.isUserLanguageJapanese ? '/mktcollections/curated/appexchange_contents' : '/learn';
  }
  get favoritesHref() {
    if (this.isAgentExchange) {
      return `${this.appExchangeBaseUrl}/${Routes.FAVORITE_LISTINGS}`;
    }
    return `/${Routes.FAVORITE_LISTINGS}`;
  }
  get helpCenterTitle() {
    return this.labels?.helpCenter?.title;
  }
  get favoritesLabel() {
    return this.labels?.favoritesAltText;
  }
  get mobileUserMenuLabel() {
    const tbidProfile = this.userContext?.tbidProfile;
    if (tbidProfile?.active) {
      const i18n = i18nStore.getState().i18n;
      return i18n?.shared?.userName?.({
        FIRST_NAME: tbidProfile.given_name,
        LAST_NAME: tbidProfile.family_name
      }) ?? this.labels?.userMenuLabel;
    }
    return this.labels?.userMenuLabel;
  }

  /** User profile image URL for avatar (desktop user menu uses tbidProfile.picture). */
  get userProfileImage() {
    return this.userContext?.tbidProfile?.picture ?? '';
  }
  get appExchangeBaseUrl() {
    return this.isUserLanguageJapanese ? APPX_JP_SITE_URL : APPX_SITE_URL;
  }
  bucketExploreUrl(slug) {
    return this.isAgentExchange ? `${this.appExchangeBaseUrl}/explore/${slug}` : `/explore/${slug}`;
  }
  get links() {
    const agentExchangeBase = this.isUserLanguageJapanese ? AGENTEXCHANGE_JP_SITE_URL : AGENTEXCHANGE_SITE_URL;
    if (this.isAgentExchange) {
      return {
        home: withLocalePrefix('/'),
        agents: withLocalePrefix('/explore/agentforce'),
        collections: withLocalePrefix('/collections'),
        consulting: `${this.appExchangeBaseUrl}/consulting`,
        learn: `${this.appExchangeBaseUrl}/learn`,
        skills: withLocalePrefix(`/${Routes.SKILLS}`)
      };
    }
    return {
      home: withLocalePrefix('/'),
      agents: `${agentExchangeBase}/explore/agentforce`,
      collections: `${agentExchangeBase}/collections`,
      consulting: withLocalePrefix('/consulting'),
      learn: withLocalePrefix(this.learnUrl)
    };
  }
  get brandText() {
    return this.labels?.brandText ?? '';
  }
  get marketplaceOptions() {
    const language = getUserLanguage();
    const domain = this.isAgentExchange ? 'agentexchange' : language === JAPANESE_LANGUAGE_CODE ? 'appexchange-jp' : 'appexchange';
    const utmSource = getSlackUtmSource(domain, language);
    const slackParams = {
      utm_source: utmSource,
      utm_medium: 'switcher'
    };
    if (this.isUserLanguageJapanese && SLACK_FORCE_MARKETPLACE_SWITCHER_JP) {
      slackParams.force_marketplace_switcher_jp = 'on';
    }
    return [{
      id: MarketplaceId.SalesforceMarketplace,
      label: this.labels.salesforceName,
      href: this.links.home
    }, {
      id: MarketplaceId.SlackMarketplace,
      label: this.labels.slackName,
      href: buildUrl(SLACK_MARKETPLACE_URL, slackParams)
    }];
  }
  get selectedMarketplace() {
    return MarketplaceId.SalesforceMarketplace;
  }
  get bucketOptions() {
    const bucketLabels = this.labels.explore.buckets;
    return [{
      icon: 'business-needs',
      title: bucketLabels.businessNeeds.title,
      description: bucketLabels.businessNeeds.description,
      action: this.bucketExploreUrl('business-needs')
    }, {
      icon: 'products',
      title: bucketLabels.products.title,
      description: bucketLabels.products.description,
      action: this.bucketExploreUrl('products')
    }, {
      icon: 'industries',
      title: bucketLabels.industries.title,
      description: bucketLabels.industries.description,
      action: this.bucketExploreUrl('industries')
    }, {
      icon: 'labs',
      title: bucketLabels.labs.title,
      description: bucketLabels.labs.description,
      action: this.bucketExploreUrl('salesforce-labs')
    }, {
      icon: 'impact',
      title: bucketLabels.impact.title,
      description: bucketLabels.impact.description,
      action: this.bucketExploreUrl('impact')
    }];
  }

  /** Nav options for ax-marketplace-header. */
  get navOptions() {
    const collectionsOption = {
      label: this.labels.collections,
      id: 'collections',
      href: this.links.collections,
      target: '_self'
    };
    const opts = [{
      label: this.labels.home,
      id: 'home',
      href: this.links.home,
      target: '_self'
    }, {
      label: this.labels.agentsNav,
      id: 'agents',
      href: this.links.agents,
      target: '_self'
    }, {
      label: this.labels.explore.apps,
      id: 'apps',
      subMenuOptions: this.bucketOptions.map(bucket => {
        const pathSegment = bucket.action.replace(/.*\/([^/]+)$/, '$1');
        const icon = BUCKET_ICON_MAP[pathSegment];
        return {
          id: pathSegment,
          icon,
          label: bucket.title,
          subtitle: bucket.description,
          href: bucket.action,
          target: '_self'
        };
      })
    }, {
      label: this.labels.consultants,
      id: 'consulting',
      href: this.links.consulting,
      target: '_self'
    }, {
      label: this.labels.learn,
      id: 'learn',
      href: this.links.learn,
      target: '_self'
    }];
    if (!this.isUserLanguageJapanese) {
      opts.splice(3, 0, collectionsOption);
    }
    if (isExternalAgentDiscoveryExperienceAvailable()) {
      opts.push({
        label: this.labels.skills,
        id: 'skills',
        href: this.links.skills,
        target: '_self'
      });
    }
    return opts;
  }

  /**
   * Handle header height change from ax-marketplace-header.
   * Sets a CSS custom property on document.body so other components can use the actual header height.
   */
  handleHeaderHeightChange(event) {
    const {
      height
    } = event.detail;
    document.body.style.setProperty('--ace-header-height', `${height}px`);
  }

  /**
   * Bridge design system navclick → site analytics (headerNavLink).
   * Fired by ax-marketplace-header when any nav link is clicked.
   */
  handleNavClick(event) {
    const {
      label,
      href,
      level,
      parentLabel
    } = event.detail;
    const navItem = level === NavLevel.L2 && parentLabel ? `${parentLabel}:${label}` : label;
    this.dispatchEvent(new CustomEvent('analytics', {
      bubbles: true,
      composed: true,
      detail: {
        eventType: 'click',
        elementType: 'headerNavLink',
        // analytics uses "url" instead of "href"
        headerNavLink: {
          label,
          url: href,
          level,
          navItem
        }
      }
    }));
  }
  get isUserLanguageJapanese() {
    return getUserLanguage() === JAPANESE_LANGUAGE_CODE;
  }

  /**
   * Opens a specific pane in the mobile menu.
   */
  openMobileMenuPane(pane) {
    const element = this.template?.querySelector('ax-marketplace-header');
    if (element) {
      element.openMobileMenuPane(pane);
    }
  }

  /**
   * Opens the custom mobile menu pane in the marketplace header.
   * @deprecated Use openMobileMenuPane(MenuPane.CUSTOM) instead
   */
  openCustomMobileMenuPane() {
    this.openMobileMenuPane(MenuPane.CUSTOM);
  }
  handleMobileHelpCenterClick() {
    this.selectedMobileMenuPane = MOBILE_MENU_PANES.HELP_CENTER;
    this.openMobileMenuPane(MenuPane.CUSTOM);
  }
  handleUserMenuClose() {
    this.openMobileMenuPane(MenuPane.MARKETPLACE_SWITCHER);
  }
  dispatchMobileLoginNavLink(navLink) {
    this.dispatchEvent(new CustomEvent('analytics', {
      bubbles: true,
      composed: true,
      detail: {
        eventType: 'click',
        elementType: 'loginNavLink',
        navLink: {
          ...navLink,
          destinationType: this.isAgentExchange ? 'external' : 'internal',
          ctaClick: false
        }
      }
    }));
  }
  handleMobileFavoritesClick(event) {
    event.preventDefault();
    this.dispatchMobileLoginNavLink({
      menuItem: FAVORITES_MENU_IDENTIFIER,
      clickText: FAVORITES_MENU_IDENTIFIER,
      clickUrl: this.favoritesHref
    });
    if (this.isAgentExchange) {
      window.location.href = this.favoritesHref;
    } else {
      navigate(this.favoritesHref);
    }
  }
  handleMobileUserMenuClick() {
    this.dispatchMobileLoginNavLink({
      menuItem: USER_MENU_IDENTIFIER,
      clickText: USER_MENU_IDENTIFIER,
      navLevel: 'l1'
    });
    this.selectedMobileMenuPane = MOBILE_MENU_PANES.USER_MENU;
    this.openMobileMenuPane(MenuPane.CUSTOM);
  }
  handleSwitchOrg(event) {
    event.preventDefault();
    this.dispatchEvent(
    // Only the mobile org card renders this control (header.html slot="utilityMobile");
    // the desktop equivalent is orgContextMenu.ts, which tags its own click "desktop".
    createEvent({
      eventType: 'click',
      elementType: ElementType.ORG_PICKER_SWITCH_ORG,
      surface: 'mobile'
    }));
    switchToOrgPicker();
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(Header, {
  track: {
    selectedMobileMenuPane: 1
  },
  wire: {
    isAgentExchange: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: siteConfigStore,
          selector: selectIsAgentExchange
        };
      }
    },
    setUserContext: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: userStore,
          selector: state => ({
            isAuthenticated: selectIsAuthenticated(state),
            tbidProfile: state.tbidProfile
          }),
          equalityFn
        };
      }
    },
    setOrgPickerFeatureFlag: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: featureFlagsStore,
          selector: state => state.featureFlags['ff-org-picker']
        };
      }
    },
    setSelectedOrg: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: orgContextStore,
          selector: selectSelectedOrg
        };
      }
    },
    setOrgCount: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: orgPickerStore,
          selector: state => Math.max(state.totalCount, state.orgs.length)
        };
      }
    },
    setupLabels: {
      adapter: WireI18n,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          selector: i18n => ({
            header: i18n.header,
            shared: selectSharedLabels(i18n),
            orgContextMenu: i18n.orgContextMenu
          })
        };
      }
    },
    setNotificationLabels: {
      adapter: WireI18n,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          selector: i18n => i18n.notifications
        };
      }
    }
  },
  fields: ["isLoggedIn", "orgPickerEnabled", "handleOrgPickerPilotChange", "handleOrgPickerPilotStorageChange", "staleSelectionClearedOrgId", "selectedOrg", "orgCount"]
});
const __lwc_component_class_internal = _registerComponent(Header, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;